import React, { ChangeEvent, useCallback, useContext, useMemo } from 'react';

import Inputs from 'components/Inputs';
import Row from 'components/Common/Row';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Dropdown from 'components/Inputs/Dropdown';
import { LabelDiv } from 'screens/Boards/PerformanceBoardForm';
import BoardSettingsHeader from '../BoardSettingsHeader/BoardSettingsHeader';
import AddFilterButton from '../AddBoardFilterButton';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import EntityDetailsContext from '../../../../../screens/EntityDetailsShow/EntityDetailsContext';
import EntityFilterToggle from '../../../../EntityFilterToggle';
import FilterPlate from '../../../../FilterPlates/FilterPlate';
import FleetOpsStaffOnly from '../../../../Common/FleetOpsStaffOnly';
import useWarningConfirmation from '../../../../ConfirmationModals/hooks/useWarningConfirmation';
import { ModalTransition } from '@atlaskit/modal-dialog';
import STORE from '../../../../../store';
import AccountPickerContext from '../../../../../contexts/AccountPickerContext';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../../../../kingpin/forms/FormInput';
import Tooltip from '../../../../Tooltip';
import Icon from '../../../../../kingpin/atoms/Icon';
import LabelsPicker from '../../../../Inputs/LabelsPicker';

const DeleteButton = ({ board }: { board: PerformanceBoardTypes.Board }) => {
  const navigate = useNavigate();
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { getConfirmationThat, DangerConfirmation } = useWarningConfirmation({
    title: `Delete ${board.name}`,
    continueText: 'Delete',
  });

  const onDeleteClicked = useCallback(() => {
    getConfirmationThat(
      'You are about to delete this board entirely from this customer, not just the area which this board is currently in. If this board is currently used in any Portals, Workspaces, Apps or Views, those will be broken by this action. Are you sure?',
    ).then((isConfirmed) => {
      if (!isConfirmed) {
        return;
      }

      const newBoard: PerformanceBoardTypes.Board = {
        ...board,
        isHidden: true,
      };

      STORE.contentDistributions
        .getPerformanceBoardsRef({ accountId: selectedAccountId })
        .doc(board.id)
        .set(newBoard)
        .then(() => {
          navigate('/');
        });
    });
  }, [board, getConfirmationThat, navigate, selectedAccountId]);

  return (
    <>
      <Button
        size={'Medium'}
        type={'Danger Secondary'}
        onClick={onDeleteClicked}
        label="Delete Board"
      />
      <ModalTransition>{DangerConfirmation}</ModalTransition>
    </>
  );
};

const BoardGeneralSettings = ({
  board,
  baseView,
  name,
  onNameChanged,
  fieldOptions,
  sortOptions,
  plates,
  setPlates,
  isEntityFilterEnabled,
  setIsEntityFilterEnabled,
  isAsset,
  setIsAsset,
  labelIds,
  setLabelIds,
  slideoutWidthOptions,
  slideoutModeOptions,
  hasUnsavedChanges,
  handleSave,
}: {
  board: PerformanceBoardTypes.Board;
  baseView: FleetOps.BaseView | undefined;
  name: string;
  onNameChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  fieldOptions:
    | {
        label: string;
        value: string;
        isSelected: boolean;
        onSelected: () => void;
      }[]
    | undefined;
  sortOptions: {
    label: string;
    value: string;
    isSelected: boolean;
    onSelected: () => void;
  }[];
  plates: FilterPlateType[];
  setPlates: React.Dispatch<React.SetStateAction<FilterPlateType[]>>;
  isEntityFilterEnabled: boolean;
  setIsEntityFilterEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isAsset: boolean;
  setIsAsset: React.Dispatch<React.SetStateAction<boolean>>;
  labelIds: string[];
  setLabelIds: React.Dispatch<React.SetStateAction<string[]>>;
  slideoutWidthOptions: {
    label: string;
    value: string;
    isSelected: boolean;
    onSelected: () => void;
  }[];
  slideoutModeOptions: {
    label: string;
    value: string;
    isSelected: boolean;
    onSelected: () => void;
  }[];
  hasUnsavedChanges: boolean;
  handleSave: () => void;
}) => {
  const entityDetailsContext = useContext(EntityDetailsContext);
  const isEntityDetailsApp = !!entityDetailsContext;
  const dataTypes = useMemo(() => (board ? [board.dataType] : []), [board]);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <BoardSettingsHeader
        title="General"
        description={`Dataset: ${
          baseView && baseView.nameAlias ? baseView.nameAlias : board.dataType
        }`}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflowY: 'scroll',
          paddingRight: 8,
          paddingLeft: 8,
          borderBottom: `1px solid var(--color-grid-border)`,
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <LabelDiv>
            <Typography.Body type="Label">Board Name</Typography.Body>
          </LabelDiv>
          <Inputs.TextInput
            value={name}
            onChange={onNameChanged}
            maxLength={40}
            data-testid={'title'}
            inputSize="Small"
          />
        </div>
        <FleetOpsStaffOnly>
          <FormInput label={'Is Asset'}>
            <Row centerAlign style={{ gap: 4 }}>
              <Inputs.Checkbox isChecked={isAsset} onToggled={setIsAsset} />
              <div style={{ cursor: 'help' }}>
                <Tooltip
                  content={
                    "Boards which are an 'Asset' will show in the 'View all Content' and 'All Assets' screens in V3 to those users who have access to them"
                  }
                >
                  <Icon icon={'help'} />
                </Tooltip>
              </div>
            </Row>
          </FormInput>
          <>
            {isAsset && (
              <FormInput label={'Labels'}>
                <LabelsPicker labelIds={labelIds} setLabelIds={setLabelIds} />
              </FormInput>
            )}
          </>
        </FleetOpsStaffOnly>
        <div style={{ marginBottom: '16px' }}>
          <LabelDiv>
            <Typography.Body type="Label">Order Table By</Typography.Body>
          </LabelDiv>
          {fieldOptions && (
            <Row>
              <div style={{ marginRight: '8px', flex: '1' }}>
                <Dropdown options={fieldOptions} />
              </div>
              <div style={{ flex: '1' }}>
                <Dropdown options={sortOptions} />
              </div>
            </Row>
          )}
        </div>
        <FleetOpsStaffOnly>
          <FormInput label={'Slideout Width'}>
            <Dropdown options={slideoutWidthOptions} />
          </FormInput>
          <FormInput label={'Slideout Display'}>
            <Row centerAlign style={{ gap: 4 }}>
              <div style={{ flex: 1 }}>
                <Dropdown options={slideoutModeOptions} />
              </div>
              <div style={{ cursor: 'help' }}>
                <Tooltip
                  content={
                    "'Squish' shrinks the grid to make room for the slideout. 'Overlay' floats the slideout over the grid at full width — scroll the grid horizontally to see the columns underneath."
                  }
                >
                  <Icon icon={'help'} />
                </Tooltip>
              </div>
            </Row>
          </FormInput>
        </FleetOpsStaffOnly>
        <LabelDiv>
          <Typography.Body type="Label">Define Scope</Typography.Body>
        </LabelDiv>

        <FilterPlatesProvider
          drillDowns={plates}
          setDrillDowns={setPlates}
          dataTypes={dataTypes}
        >
          <Row style={{ marginBottom: '16px' }}>
            <AddFilterButton text="Add Filter" />
          </Row>
        </FilterPlatesProvider>
        {isEntityDetailsApp && (
          <div style={{ marginTop: 16 }}>
            <EntityFilterToggle
              isEntityFilterEnabled={isEntityFilterEnabled}
              setIsEntityFilterEnabled={setIsEntityFilterEnabled}
            />
          </div>
        )}

        {plates.length !== 0 && (
          <FilterPlatesProvider
            drillDowns={plates}
            setDrillDowns={setPlates}
            dataTypes={dataTypes}
          >
            <div
              style={{
                marginBottom: '32px',
                paddingTop: '1px',
                paddingLeft: '1px',
              }}
            >
              {plates.map((d) => (
                <div key={d.id} style={{ marginBottom: '8px' }}>
                  <FilterPlate plate={d} isScope={false} />
                </div>
              ))}
            </div>
          </FilterPlatesProvider>
        )}
      </div>

      <Row
        spaceBetween
        centerAlign
        style={{ padding: '8px 0', height: '46px' }}
      >
        <FleetOpsStaffOnly fallback={<div />}>
          <DeleteButton board={board} />
        </FleetOpsStaffOnly>
        <Button
          type="Primary"
          size="Small"
          isDisabled={name.trim() === '' || !hasUnsavedChanges}
          onClick={handleSave}
          label="Save"
        />
      </Row>
    </div>
  );
};

export default BoardGeneralSettings;
